import { type CSSProperties } from 'react';
import { spacing } from 'dibs-sassy/exports/spacing.ts';

import { type ScrollPadding, type GapSize } from '../../types.ts';

const gapSizes: Record<GapSize, string> = {
    none: '1px',
    xSmall: spacing.sassySpacingXSmall,
    smaller: spacing.sassySpacingSmaller,
    small: spacing.sassySpacingSmall,
    medium: spacing.sassySpacingMedium,
    large: spacing.sassySpacingLarge,
};

const scrollPaddingSizes: Record<ScrollPadding, string> = {
    none: '0px',
    small: spacing.sassySpacingSmall,
    large: spacing.sassySpacingLarge,
    // Spacer pseudo elements take up the same track space as the padding based sizes
    smallEdges: spacing.sassySpacingSmall,
};

export const adjustFlexBasis = ({
    scrollPadding,
    gapSize,
    itemsToShow,
}: {
    scrollPadding: ScrollPadding;
    gapSize: GapSize;
    itemsToShow: number;
}): string => {
    if (scrollPadding !== 'none') {
        return `calc((100% + ${scrollPaddingSizes[scrollPadding]} - ${gapSizes[gapSize]} / 2) / ${itemsToShow})`;
    } else if (gapSize !== 'none' && itemsToShow % 1 !== 0) {
        return `calc((100% - (${gapSizes[gapSize]} / 2)) / ${itemsToShow})`;
    }

    return `${100 / itemsToShow}%`;
};

export const getCarouselItemStyle = ({
    itemFlexBasis,
    shouldAdjustFlexBasis,
    scrollPadding,
    gapSize,
    itemsToShow,
}: {
    itemFlexBasis: string | undefined;
    shouldAdjustFlexBasis: boolean;
    scrollPadding: ScrollPadding;
    gapSize: GapSize;
    itemsToShow: number;
}): CSSProperties | undefined => {
    if (itemFlexBasis) {
        // Items grow to fill leftover track space unless width is pinned too
        return { flexBasis: itemFlexBasis, width: itemFlexBasis, maxWidth: itemFlexBasis };
    }

    return shouldAdjustFlexBasis
        ? { flexBasis: adjustFlexBasis({ scrollPadding, gapSize, itemsToShow }) }
        : undefined;
};
